import { Role, Permission } from '@/types';
import { isPermission, isRole, isPermissionArray } from '@/lib/type-guards';

/**
 * Système RBAC - Role-Based Access Control
 * Définit les permissions par rôle
 */

export const rolePermissions: Record<Role, Permission[]> = {
  [Role.ADMIN]: [
    // Permissions de gestion
    Permission.VIEW_ALL_PROJECTS,
    Permission.MANAGE_USERS,
    Permission.MANAGE_CLIENTS,
    Permission.MANAGE_PRESTATAIRES,
    Permission.VALIDATE_INVOICES,
    Permission.VIEW_FINANCIALS,
    Permission.MANAGE_SETTINGS,
    Permission.VIEW_ASSIGNED_PROJECTS,
    Permission.EDIT_PROJECT_METRICS,
    Permission.VIEW_PROJECT_INVOICES,
    Permission.VIEW_PROJECT_STATUS,
    Permission.VIEW_KEY_METRICS,
    Permission.VIEW_MARGINS,
    Permission.CREATE_PROVIDER,
    // Nouvelles permissions RBAC
    Permission.EDIT_EDITORIAL_CALENDAR,
    Permission.VIEW_BUDGET_DETAILS,
    Permission.VIEW_AGENCY_FEES,
    Permission.VIEW_MARGINS_OVERVIEW,
    Permission.ACCESS_ADMIN_DASHBOARD,
  ],
  [Role.CHEF_PROJET]: [
    // Comportement par défaut (toujours actif, non modifiable via UI)
    Permission.VIEW_ASSIGNED_PROJECTS,
    Permission.EDIT_PROJECT_METRICS,
    Permission.VIEW_PROJECT_STATUS,
    Permission.VIEW_KEY_METRICS,
    Permission.EDIT_EDITORIAL_CALENDAR,
    Permission.ACCESS_CHEF_DASHBOARD,
    // Permissions optionnelles (personnalisables via UI)
    // VIEW_MARGINS, VALIDATE_INVOICES, CREATE_PROVIDER, ACT_AS_PROVIDER, VIEW_BUDGET_DETAILS
  ],
  [Role.CLIENT]: [
    Permission.VIEW_OWN_PROJECTS,
    Permission.VIEW_PROJECT_STATUS,
    Permission.VIEW_KEY_METRICS,
    Permission.ACCESS_CLIENT_DASHBOARD,
  ],
  [Role.PRESTATAIRE]: [
    Permission.ACT_AS_PROVIDER, // Permission combo qui inclut upload, view et payment status
    Permission.UPLOAD_INVOICES,
    Permission.VIEW_OWN_INVOICES,
    Permission.VIEW_PAYMENT_STATUS,
    Permission.ACCESS_PROVIDER_DASHBOARD,
  ],
};

/**
 * Vérifie si un rôle a une permission spécifique
 * Note: Cette fonction ne vérifie que les permissions par défaut du rôle.
 * Pour vérifier les permissions personnalisées d'un utilisateur, utilisez hasUserPermission()
 */
export function hasPermission(role: Role, permission: Permission): boolean {
  if (!isRole(role) || !isPermission(permission)) {
    return false;
  }
  return rolePermissions[role]?.includes(permission) ?? false;
}

/**
 * Vérifie si un utilisateur a une permission spécifique (inclut permissions personnalisées)
 * Combine les permissions du rôle ET les permissions personnalisées de l'utilisateur
 */
export function hasUserPermission(
  role: Role,
  userPermissions: Permission[] | undefined,
  permission: Permission
): boolean {
  if (!isRole(role) || !isPermission(permission)) {
    return false;
  }
  
  // Vérifier d'abord les permissions du rôle
  if (hasPermission(role, permission)) {
    return true;
  }
  
  // Ensuite vérifier les permissions personnalisées
  if (!userPermissions) return false;
  
  // Valider que toutes les permissions sont valides
  const validPermissions = userPermissions.filter(isPermission);
  return validPermissions.includes(permission);
}

/**
 * Vérifie si un rôle a toutes les permissions spécifiées
 */
export function hasAllPermissions(role: Role, permissions: Permission[]): boolean {
  if (!isRole(role) || !isPermissionArray(permissions)) {
    return false;
  }
  return permissions.every(permission => hasPermission(role, permission));
}

/**
 * Vérifie si un rôle a au moins une des permissions spécifiées
 */
export function hasAnyPermission(role: Role, permissions: Permission[]): boolean {
  if (!isRole(role) || !isPermissionArray(permissions)) {
    return false;
  }
  return permissions.some(permission => hasPermission(role, permission));
}

/**
 * Récupère toutes les permissions d'un rôle
 */
export function getRolePermissions(role: Role): Permission[] {
  if (!isRole(role)) {
    return [];
  }
  return rolePermissions[role] ?? [];
}

/**
 * Vérifie si un utilisateur peut accéder aux projets (via n'importe quel type d'accès)
 * Utile pour déterminer si l'utilisateur doit voir le menu "Projets"
 */
export function canAccessProjects(role: Role, permissions?: Permission[]): boolean {
  return (
    hasUserPermission(role, permissions, Permission.VIEW_ALL_PROJECTS) ||
    hasUserPermission(role, permissions, Permission.VIEW_ASSIGNED_PROJECTS) ||
    hasUserPermission(role, permissions, Permission.VIEW_OWN_PROJECTS)
  );
}

/**
 * Vérifie si un utilisateur peut agir en tant que prestataire (upload factures, etc.)
 * Utile pour déterminer si l'utilisateur doit voir le menu "Factures Prestataire"
 */
export function canActAsProvider(role: Role, permissions?: Permission[]): boolean {
  return (
    hasUserPermission(role, permissions, Permission.UPLOAD_INVOICES) ||
    hasUserPermission(role, permissions, Permission.ACT_AS_PROVIDER)
  );
}